import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, FlaskConical, MapPin, Printer, ScrollText, Truck } from 'lucide-react'
import { useProduct, useProductAudit, useProductRecord } from '@/hooks/useProducts'
import { useLisResultsForProduct } from '@/hooks/useLisOrders'
import { usePatientRecord } from '@/hooks/usePatientRecord'
import { formatLimsValue, hasLimsResults, LIMS_RESULT_GROUPS, limsRowsOf } from '@/lib/limsResults'
import { dinCheckCharacter, isIsbtDin } from '@/lib/isbtDin'
import {
  catalogName,
  custodyEvents,
  divisionLabel,
  doseCards,
  inventoryState,
  releaseState,
  timeInStorage,
} from '@/lib/productRecord'
import { categoryOf, collectedAt, resultStatus } from '@/lib/patientLis'
import { procedureRunPath } from '@/lib/procedureRunPath'
import { isShippable } from '@/lib/shipping'
import { isSuperseded } from '@/lib/productSupersession'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { LisResultsPanel } from '@/components/procedure/LisResultsPanel'
import { LisReleasePanel } from '@/components/procedure/LisReleasePanel'
import { AuditTrailTable } from '@/components/audit/AuditTrailTable'
import { LimsDetailsDialog } from '@/components/procedure/LimsDetailsDialog'
import { LabelPrintingDialog } from '@/components/procedure/LabelPrintingDialog'
import { ProductLabelPreviewDialog, storedSelectionsOf } from '@/components/procedure/ProductLabelPreviewDialog'
import { MoveProductDialog } from '@/components/inventory/storage/MoveProductDialog'
import { ShipProductDialog } from '@/components/shipping/ShipProductDialog'
import {
  Card,
  CapsHeading,
  DinLink,
  Fact,
  formatDate,
  formatDateTime,
  formatShortMoment,
  INK,
  LINK,
  MUTED,
  NotRecorded,
  RecordButton,
  RecordCard,
  RecordLink,
  RecordTable,
  SectionCard,
  Td,
  TonePill,
  useCanOpenPatients,
} from '@/components/record/RecordParts'
import { useLocationText } from '@/hooks/useStoragePlaces'
import type { LisResult } from '@/api/lisApi'
import type { ProcedureType } from '@/types/cgt'
import type { ProductLabelSelections } from '@/lib/digitrax'
import type { ProductRecord } from '@/types/products'
import type { RecordedDonor } from '@/api/patientsApi'

/**
 * One product — `/products/:id` (z8nrz7dxeu; the full record, z8nrz7g57f).
 *
 * Everything known about one bag, from the product side, laid out as the
 * approved mock: a header band (DIN, name, pills, dose per kg, actions), then a
 * main column (cell counts, microbiology, laboratory results, release, trail)
 * beside a column of record cards. Every DIN in the app links here and the
 * recipient links to the patient page, so the two records open each other.
 *
 * The mock was drawn ahead of the data. What this page shows instead, from
 * what is recorded:
 *  - release is the run's product release review form — there is no release
 *    flag on a product — and is omitted for a run that has none;
 *  - "intended use", "donation type" and the label's ABO are the label's
 *    select messages, the only place each is recorded;
 *  - dose per kg is the server's calculation (single platform, else dual),
 *    not a "viable CD34+" the system does not measure;
 *  - the stage columns are the LIS orders' stages, beside the product's own
 *    recorded results;
 *  - expiry, collection facility name, temperature readings and "last
 *    scanned" are not recorded anywhere, so they are not shown;
 *  - "Transfer" is the app's Move (re-file in storage) and Ship (to a partner),
 *    and "Issue for infusion" is not offered: no issue step exists.
 */
export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>()
  const product = useProduct(id ?? null)
  const record = useProductRecord(id ?? null)
  const audit = useProductAudit(id ?? null)
  const lis = useLisResultsForProduct(id ?? null)
  const locate = useLocationText()
  const { can } = usePermission()

  const p = product.data
  const r = record.data
  // The recipient's clinical facts (ABO, donor, Day 0) come from the patient
  // record, which needs procedures.read; this page needs only inventory access.
  // Without it the record is not asked for, and the label's facts stand alone.
  const canReadPatientRecord = can(PERMISSIONS.proceduresRead)
  const patient = usePatientRecord(canReadPatientRecord ? (p?.patientMrn ?? null) : null)
  const canOpenPatients = useCanOpenPatients()

  const [limsOpen, setLimsOpen] = useState(false)
  const [choosingLabel, setChoosingLabel] = useState(false)
  const [printing, setPrinting] = useState<{ kind: 'reprint' | 'relabel'; selections?: ProductLabelSelections } | null>(null)
  const [moving, setMoving] = useState(false)
  const [shipping, setShipping] = useState(false)

  const parentAudit = useProductAudit(record.data?.parent?.id ?? null)
  const custody = useMemo(() => custodyEvents(audit.data ?? [], parentAudit.data ?? []), [audit.data, parentAudit.data])
  const scrollTo = (elementId: string) => document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  if (product.isLoading) {
    return <div className={cn('min-h-full bg-[#F4F6F9] py-16 text-center text-sm', MUTED)}>Loading…</div>
  }
  if (product.error || !p) {
    /* A product id in an audit row is a historical fact; the row it named can
       still have been removed, or belong to the tenant just switched away from.
       Say which, rather than rendering an empty page. */
    return (
      <div className='min-h-full bg-[#F4F6F9] px-6 py-8'>
        <BackToProducts />
        <p className={cn('py-16 text-center text-sm', MUTED)}>
          This product could not be loaded. It may have been removed, or belong to another tenant.
        </p>
      </div>
    )
  }

  const name = catalogName(p.catalogDescription ?? r?.catalog.description)
  const division = divisionLabel(r?.division, r?.siblings.length)
  const state = inventoryState(p)
  const release = releaseState(r?.release)
  const superseded = isSuperseded(p)
  const stored = !!p.storagePath
  const doses = doseCards(p.limsValues)
  const selections = p.labelSelections
  const clinical = patient.data?.clinical
  // A fact the product record has not answered yet — or could not — is said so,
  // never shown as "not recorded" (a release state above all).
  const recordGap = record.isPending ? 'Loading…' : record.isError ? 'Could not be loaded' : null
  // Only for a record that is asked for: a query never sent stays pending.
  const askedPatient = !!p.patientMrn && canReadPatientRecord
  const patientGap = askedPatient && patient.isPending ? 'Loading…' : askedPatient && patient.isError ? 'Could not be loaded' : null

  return (
    <div className={cn('min-h-full bg-[#F4F6F9]', INK)}>
      {/* Header band */}
      <section className='border-b border-[#E4E7EC] bg-white px-6 py-5'>
        <div className='mx-auto max-w-[1392px]'>
          <BackToProducts />
          <div className='flex flex-wrap items-start justify-between gap-x-8 gap-y-5'>
            <div className='flex min-w-0 flex-col gap-2.5'>
              <div className={cn('text-xs font-bold uppercase tracking-[0.1em]', MUTED)}>Product</div>
              <div className='flex flex-wrap items-center gap-3'>
                <h1 className='m-0 font-mono text-[28px] font-semibold leading-tight'>
                  {p.din}
                  {isIsbtDin(p.din) && ` ${p.dinFlag ?? '00'}`}
                </h1>
                {/* The ISBT 128 check character, boxed as it is printed (z8nrz7fxwh). */}
                {isIsbtDin(p.din) && (
                  <span
                    className='rounded border-2 border-[#1D2939] px-1.5 font-mono text-lg'
                    title='ISBT 128 check character'
                    data-testid='product-din-flag-check'
                  >
                    {dinCheckCharacter(p.din)}
                  </span>
                )}
              </div>
              <div className='text-lg font-semibold'>
                {name ?? p.productDescriptionCode}
                {division && <span className={cn('font-medium', MUTED)}> · {division}</span>}
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                {r?.catalog.preservationState && <TonePill tone='blue'>{r.catalog.preservationState}</TonePill>}
                {selections?.donationType && <TonePill tone='gray'>{selections.donationType}</TonePill>}
                {release && <TonePill tone={release.tone}>{release.label}</TonePill>}
                <TonePill tone={state.tone}>{state.label}</TonePill>
                {selections?.intendedUse && (
                  <span
                    className='rounded border-[1.5px] border-[#1D2939] px-3 py-1 text-[12.5px] font-bold uppercase tracking-[0.06em]'
                    title='Intended use, as printed on the label'
                  >
                    {selections.intendedUse}
                  </span>
                )}
              </div>
            </div>

            <div className='flex flex-col gap-2.5'>
              <div className={cn('flex justify-between gap-4 text-xs font-bold uppercase tracking-[0.08em]', MUTED)}>
                <span>Dose per kg of recipient</span>
                <span className='normal-case tracking-normal'>
                  {recordGap ?? (r?.recipientWeightKg != null ? `${r.recipientWeightKg} kg` : 'No recipient weight')}
                </span>
              </div>
              <div className='grid grid-cols-[repeat(3,minmax(120px,1fr))] gap-2.5'>
                {doses.map((d) => (
                  <div key={d.label} className='rounded-lg border border-[#E4E7EC] px-3.5 py-3'>
                    <div className={cn('text-[13px]', MUTED)}>{d.label}</div>
                    <div className='text-[28px] font-bold leading-tight tabular-nums'>{d.value ?? '—'}</div>
                    <div className={cn('text-xs', MUTED)}>{d.unit}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className='flex flex-wrap gap-2.5 self-end'>
              {can(PERMISSIONS.productsManage) && !superseded && (
                <RecordButton
                  onClick={() =>
                    selections ? setPrinting({ kind: 'reprint', selections: storedSelectionsOf(p) }) : setChoosingLabel(true)
                  }
                >
                  <Printer className='h-4 w-4' />
                  Print label
                </RecordButton>
              )}
              {/* Transfer, as the app does it: re-file within storage, or ship to a partner. */}
              {can(PERMISSIONS.productsManage) && !superseded && stored && (
                <RecordButton onClick={() => setMoving(true)}>
                  <MapPin className='h-4 w-4' />
                  Move
                </RecordButton>
              )}
              {can(PERMISSIONS.productsShip) && !superseded && isShippable(p) && (
                <RecordButton onClick={() => setShipping(true)}>
                  <Truck className='h-4 w-4' />
                  Ship
                </RecordButton>
              )}
              <RecordButton onClick={() => scrollTo('audit-trail')}>
                <ScrollText className='h-4 w-4' />
                Audit trail
              </RecordButton>
              {hasLimsResults(p.limsValues) && (
                <RecordButton onClick={() => setLimsOpen(true)}>
                  <FlaskConical className='h-4 w-4' />
                  LIMS results
                </RecordButton>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className='mx-auto flex max-w-[1392px] flex-wrap items-start gap-6 p-6'>
        <div className='flex min-w-0 flex-[999_1_640px] flex-col gap-6'>
          <CellCounts
            limsValues={p.limsValues}
            results={lis.data ?? []}
            weightKg={r?.recipientWeightKg ?? null}
            weightKnown={!recordGap}
            divided={(r?.siblings.length ?? 0) > 1}
            onWorksheets={() => scrollTo('lis-worksheets')}
          />
          <Microbiology results={lis.data ?? []} />
          <SectionCard
            id='lis'
            size='md'
            title='LIS results for this DIN'
            subtitle={
              <>
                Every laboratory order filed against this product
                {lis.data?.length ? ` · ${lis.data.length} ${lis.data.length === 1 ? 'result' : 'results'}` : ''}
              </>
            }
            flush
          >
            <LisSummary results={lis.data} isLoading={lis.isPending} isError={lis.isError} />
          </SectionCard>
          <SectionCard id='lis-worksheets' size='md' title='Laboratory worksheets' subtitle='Each result with every value the laboratory sent'>
            <LisResultsPanel productId={p.id} />
          </SectionCard>
          <SectionCard size='md' title='Release results to Epic'>
            <LisReleasePanel productId={p.id} />
          </SectionCard>
          <SectionCard id='audit-trail' size='md' title='Audit trail' subtitle='Every change to this product, newest first' flush>
            <div className='px-5 pb-5'>
              <AuditTrailTable
                rows={audit.data ?? []}
                isLoading={audit.isLoading}
                isError={audit.isError}
                emptyMessage='Nothing recorded for this product yet.'
              />
            </div>
          </SectionCard>
        </div>

        <aside className='flex min-w-0 flex-[1_1_380px] flex-col gap-4'>
          <RecordCard title='Recipient & donor'>
            <Fact label='Intended recipient'>
              {/* Kit forms carry no patient (`requiresPatientHistory: false`). */}
              {p.patientMrn ? (
                <>
                  {canOpenPatients ? (
                    <RecordLink to={`/patients/${encodeURIComponent(p.patientMrn)}`}>{p.patientName?.trim() || 'Name not recorded'}</RecordLink>
                  ) : (
                    <span>{p.patientName?.trim() || 'Name not recorded'}</span>
                  )}{' '}
                  · MRN{' '}
                  <span className='font-mono text-[12.5px]'>{p.patientMrn}</span>
                </>
              ) : (
                <NotRecorded>No patient (kit product)</NotRecorded>
              )}
            </Fact>
            {p.patientMrn && (
              <Fact label='Recipient born / ABO'>
                {[formatDate(p.patientDob), clinical?.recipient_abo_rh?.value ?? (patientGap ? `ABO ${patientGap.toLowerCase()}` : null)]
                  .filter(Boolean)
                  .join(' · ')}
              </Fact>
            )}
            <Fact label='Recipient weight'>
              {recordGap ? <NotRecorded>{recordGap}</NotRecorded> : r?.recipientWeightKg != null ? `${r.recipientWeightKg} kg` : null}
            </Fact>
            <Fact label='Donor'>
              {patientGap || recordGap ? (
                <NotRecorded>{patientGap ?? recordGap}</NotRecorded>
              ) : (
                donorOf(patient.data?.donors ?? [], r?.procedure?.id, p.patientName) ?? selectionsDonor(selections?.donationType)
              )}
            </Fact>
            <Fact label='Planned use'>
              {r?.procedure ? <RunLink procedure={r.procedure} /> : null}
              {clinical?.planned_infusion_date && (
                <span className={cn('block text-[13px]', MUTED)}>
                  Day 0 {formatDate(clinical.planned_infusion_date.value) ?? clinical.planned_infusion_date.value}
                </span>
              )}
            </Fact>
          </RecordCard>

          <RecordCard title='Identification'>
            <Fact label='ISBT product code'>
              <span className='font-mono text-[12.5px]'>{r?.catalog.productDescriptionCode ?? p.productDescriptionCode}</span>
            </Fact>
            <Fact label='Division'>
              {r?.division ? (
                <>
                  {r.division}
                  {r.siblings.length > 1 && (
                    <span className={cn('block text-[13px]', MUTED)}>
                      Also{' '}
                      {r.siblings
                        .filter((s) => s.id !== p.id)
                        .map((s, i) => (
                          <span key={s.id}>
                            {i > 0 && ', '}
                            <Link className={LINK} to={`/products/${s.id}`}>
                              {s.division ?? s.productDescriptionCode}
                            </Link>
                          </span>
                        ))}
                    </span>
                  )}
                </>
              ) : (
                <NotRecorded>{recordGap ?? 'Not divided'}</NotRecorded>
              )}
            </Fact>
            <Fact label='Donation type'>{selections?.donationType}</Fact>
            {/* The DIN's first five characters are the facility identification number. */}
            <Fact label='Facility (FIN)'>{isIsbtDin(p.din) ? <span className='font-mono text-[12.5px]'>{p.din.slice(0, 5)}</span> : null}</Fact>
            <Fact label='Created'>{formatDateTime(p.createdAt)}</Fact>
            <Fact label='ABO / Rh on label'>{selections?.bloodType}</Fact>
          </RecordCard>

          <RecordCard title='Quality & release'>
            <Fact label='Release'>
              {release ? (
                <TonePill tone={release.tone}>{release.label}</TonePill>
              ) : (
                <NotRecorded>{recordGap ?? 'No release review on this run'}</NotRecorded>
              )}
            </Fact>
            {r?.catalog.quarantine && <Fact label='Quarantine'>{r.catalog.quarantine}</Fact>}
            <Fact label='Product code'>{superseded ? 'Superseded by a new product code' : 'Current'}</Fact>
          </RecordCard>

          <RecordCard title='Inventory & storage'>
            <Fact label='Inventory status'>
              <TonePill tone={state.tone}>{state.label}</TonePill>
            </Fact>
            {p.patientMrn && (
              <Fact label='Reserved for'>
                {`${p.patientName ?? 'Patient'}${/autolog/i.test(selections?.donationType ?? '') ? ' (autologous)' : ''}`}
              </Fact>
            )}
            {r?.storage?.unit && (
              <Fact label='Storage device'>
                {[r.storage.unit.equipmentLabel ?? r.storage.unit.name ?? r.storage.unit.code, r.storage.unit.equipmentType].filter(Boolean).join(' · ')}
              </Fact>
            )}
            {p.storagePath && <Fact label='Location'>{locate(p.storagePath)}</Fact>}
            {p.storagePath && (
              <Fact label='Storage condition'>
                {r?.storage?.condition ? [r.storage.condition.label, r.storage.condition.temperature].filter(Boolean).join(' · ') : null}
              </Fact>
            )}
            <Fact label='Required temperature'>{r?.catalog.storageTemperature}</Fact>
            {p.storagePath && <Fact label='Placed in storage'>{formatDateTime(r?.storedAt)}</Fact>}
            {p.storagePath && <Fact label='Time in storage'>{timeInStorage(r?.storedAt)}</Fact>}
            <Fact label='Containers'>{division ? `1 bag · ${division}` : '1 bag'}</Fact>
            {p.locationPartner && <Fact label='Shipped to'>{`${p.locationPartner.name} (${p.locationPartner.code})`}</Fact>}
          </RecordCard>

          <RecordCard title='Composition & processing'>
            <Fact label='Final volume'>{p.volumeMl != null ? `${p.volumeMl} mL` : null}</Fact>
            <Fact label='Anticoagulant'>{r?.catalog.anticoagulant}</Fact>
            <Fact label='Cryoprotectant'>{r?.catalog.cryoprotectant}</Fact>
            <Fact label='Container'>{[r?.catalog.containers, r?.catalog.finalContainer].filter(Boolean).join(' · ')}</Fact>
            {r?.catalog.processing && <Fact label='Processing'>{r.catalog.processing}</Fact>}
            {r?.catalog.collectionDetail && <Fact label='Collection'>{r.catalog.collectionDetail}</Fact>}
            <Fact label='Made in'>{p.associatedForm?.name}</Fact>
            {r?.parent && (
              <Fact label='Split from'>
                <DinLink productId={r.parent.id} din={`${r.parent.din} · ${r.parent.productDescriptionCode}`} />
              </Fact>
            )}
            {(r?.children.length ?? 0) > 0 && (
              <Fact label='Divided into'>
                {r!.children.map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && ', '}
                    <Link className={LINK} to={`/products/${c.id}`}>
                      {c.division ?? c.productDescriptionCode}
                    </Link>
                  </span>
                ))}
              </Fact>
            )}
          </RecordCard>

          <Card className='flex flex-col gap-3.5 p-5'>
            <CapsHeading>Chain of custody</CapsHeading>
            {audit.isLoading || parentAudit.isLoading ? (
              <p className={cn('m-0 text-sm', MUTED)}>Loading…</p>
            ) : audit.isError || parentAudit.isError ? (
              // A partial chain (the parent's half missing) would read as complete.
              <p className={cn('m-0 text-sm', MUTED)}>The chain of custody could not be loaded. Refresh the page to try again.</p>
            ) : custody.length === 0 ? (
              <p className={cn('m-0 text-sm', MUTED)}>Nothing recorded yet.</p>
            ) : (
              <ol className='m-0 flex list-none flex-col gap-3.5 p-0'>
                {custody.map((e) => (
                  <li key={e.id} className='flex gap-3'>
                    <span className='mt-[5px] h-2.5 w-2.5 flex-none rounded-full bg-[#1F7BC6]' aria-hidden='true' />
                    <div className='min-w-0'>
                      <div className='font-semibold'>{e.title}</div>
                      <div className={cn('text-[13px]', MUTED)}>
                        {[formatDateTime(e.at), e.by].filter(Boolean).join(' · ')}
                      </div>
                      {e.detail && <div className={cn('text-[13px]', MUTED)}>{e.detail}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </aside>
      </div>

      <LimsDetailsDialog product={limsOpen ? p : null} onClose={() => setLimsOpen(false)} />
      <LabelPrintingDialog
        product={choosingLabel ? p : null}
        onClose={() => setChoosingLabel(false)}
        onPrinted={(_product, chosen) => {
          setChoosingLabel(false)
          setPrinting({ kind: 'relabel', selections: chosen })
        }}
      />
      <ProductLabelPreviewDialog
        product={printing ? p : null}
        selections={printing?.selections}
        printKind={printing?.kind}
        onClose={() => setPrinting(null)}
      />
      <MoveProductDialog product={moving && p.storagePath ? p : null} onClose={() => setMoving(false)} />
      <ShipProductDialog open={shipping} onClose={() => setShipping(false)} initialProducts={[p]} />
    </div>
  )
}

function BackToProducts() {
  return (
    <Link to='/products?tab=products' className={cn('mb-3 inline-flex items-center gap-1.5 text-sm hover:text-[#1D2939]', MUTED)}>
      <ArrowLeft className='h-4 w-4' />
      Products
    </Link>
  )
}

/** The run a product was made in, linked when it has a page. */
function RunLink({ procedure }: { procedure: NonNullable<ProductRecord['procedure']> }) {
  const to = procedureRunPath(procedure.type as ProcedureType, procedure.id, procedure.patientMrn)
  return to ? <RecordLink to={to}>{procedure.name}</RecordLink> : <>{procedure.name}</>
}

/** The donor recorded on the run's forms: name or type, and relationship. A self-donor is the patient. */
function donorOf(donors: RecordedDonor[], procedureId: string | null | undefined, patientName: string | null | undefined): string | null {
  const d = (procedureId && donors.find((x) => x.procedureIds.includes(procedureId))) || null
  if (!d) return null
  if (/autolog|self/i.test(`${d.values.donor_type ?? ''} ${d.values.relationship ?? ''}`)) return 'Self (autologous)'
  const name = [d.values.donor_first_name, d.values.donor_last_name].filter(Boolean).join(' ') || null
  return [name ?? patientName ?? null, d.values.relationship ?? d.values.donor_type].filter(Boolean).join(' · ') || null
}

/** With no donor form, the label's donation type is what is known ("Autologous" means self). */
function selectionsDonor(donationType: string | null | undefined): string | null {
  if (!donationType) return null
  return /autolog/i.test(donationType) ? 'Self (autologous)' : donationType
}

/** The mock's measure groups, read off an observation's name, in the order it lists them. */
const MEASURE_GROUPS: { title: string; test: RegExp }[] = [
  { title: 'CD34+ hematopoietic progenitors', test: /cd34/i },
  { title: 'CD3+ T cells', test: /cd3\b/i },
  { title: 'Red cells', test: /hct|hematocrit|rbc|red cell/i },
  { title: 'Total nucleated cells (TNC)', test: /tnc|nucleated|volume|wbc|viab/i },
]
const GROUP_ORDER = ['Total nucleated cells (TNC)', 'CD34+ hematopoietic progenitors', 'CD3+ T cells', 'Red cells', 'Other measures']

/** The first group whose rule names this measure; CD34 before TNC, so "CD34+ of viable CD45+" is CD34. */
function groupOf(name: string): string {
  return MEASURE_GROUPS.find((g) => g.test.test(name))?.title ?? 'Other measures'
}

/**
 * Cell counts and viability: the LIS results by order stage — the mock's As
 * collected / Pre-cryo / Post-thaw — grouped as the mock groups them, then the
 * results recorded for this product (its own calculated figures).
 */
function CellCounts({
  limsValues,
  results,
  weightKg,
  weightKnown,
  divided,
  onWorksheets,
}: {
  limsValues: unknown
  results: LisResult[]
  weightKg: number | null
  /** The product record has answered; until then nothing is said about the weight. */
  weightKnown: boolean
  divided: boolean
  onWorksheets: () => void
}) {
  const matrix = useMemo(() => stageMatrix(results.filter((r) => categoryOf(r) === 'cell_counts')), [results])
  const recorded = LIMS_RESULT_GROUPS.map((g) => ({ title: g.title, rows: limsRowsOf(limsValues, g.keys) })).filter((g) => g.rows.length)
  if (!matrix.stages.length && !recorded.length) return null
  const sections = GROUP_ORDER.map((title) => ({ title, rows: matrix.rows.filter((row) => groupOf(row.name) === title) })).filter(
    (g) => g.rows.length,
  )
  const sectionRow = (title: string, span: number) => (
    <tr key={title}>
      <td colSpan={span} className='bg-[#F2F4F7] px-3.5 py-2.5 text-[13px] font-bold text-[#344054]'>
        {title}
      </td>
    </tr>
  )
  return (
    <SectionCard
      size='md'
      title='Cell counts & viability'
      actions={
        results.length > 0 ? (
          <button type='button' className={cn(LINK, 'text-sm')} onClick={onWorksheets}>
            LIS worksheets
          </button>
        ) : undefined
      }
      flush
    >
      {matrix.stages.length > 0 && (
        <RecordTable
          minWidth={640}
          numeric={matrix.stages.map((_, i) => i + 1)}
          head={[
            'Measure',
            ...matrix.stages.map((s) => (
              <span key={s.name} className='inline-block leading-tight'>
                {s.name}
                {s.at && (
                  <>
                    <br />
                    {formatShortMoment(s.at)}
                  </>
                )}
              </span>
            )),
          ]}
        >
          {sections.flatMap((g) => [
            sectionRow(g.title, matrix.stages.length + 1),
            ...g.rows.map((row) => (
              <tr key={row.key}>
                <Td>
                  <div className='font-semibold'>{row.name}</div>
                  {(row.units || row.ref) && (
                    <div className={cn('text-xs', MUTED)}>{[row.units, row.ref && `spec ${row.ref}`].filter(Boolean).join(' · ')}</div>
                  )}
                </Td>
                {matrix.stages.map((s) => (
                  <Td key={s.name} className='text-right tabular-nums'>
                    {row.values[s.name] ?? '—'}
                  </Td>
                ))}
              </tr>
            )),
          ])}
        </RecordTable>
      )}
      {recorded.length > 0 && (
        <RecordTable minWidth={480} numeric={[1]} head={['Recorded for this product', 'Value']}>
          {recorded.flatMap((g) => [
            sectionRow(g.title, 2),
            ...g.rows.map((row) => (
              <tr key={row.key}>
                <Td className='font-semibold'>{row.label}</Td>
                <Td className='text-right tabular-nums'>{formatLimsValue(row.value)}</Td>
              </tr>
            )),
          ])}
        </RecordTable>
      )}
      <p className={cn('m-0 px-5 py-3.5 text-[13px] leading-normal', MUTED)}>
        {!weightKnown
          ? ''
          : weightKg != null
            ? `Per-kg values use the recipient weight on the run's forms, ${weightKg} kg. `
            : 'No recipient weight is recorded on the run’s forms, so per-kg values cannot be given. '}
        Stage columns are the laboratory results filed against this product, by the stage of each order.
        {divided && ' Values recorded before the split are for the whole collection.'}
      </p>
    </SectionCard>
  )
}

/** Observations as rows, LIS order stages as columns (oldest stage first); the newest value per cell. */
function stageMatrix(results: LisResult[]) {
  const stages: { name: string; at: string | null }[] = []
  const rows = new Map<string, { key: string; name: string; units: string | null; ref: string | null; values: Record<string, string> }>()
  const oldestFirst = [...results].sort((a, b) => collectedAt(a).localeCompare(collectedAt(b)))
  for (const r of oldestFirst) {
    const stage = r.stage ?? 'No stage'
    if (!stages.some((s) => s.name === stage)) stages.push({ name: stage, at: collectedAt(r) })
    for (const o of r.observations) {
      const row = rows.get(o.code) ?? { key: o.code, name: o.codeText ?? o.code, units: o.units, ref: o.referenceRange, values: {} }
      if (o.value != null && o.value !== '') row.values[stage] = o.value
      rows.set(o.code, row)
    }
  }
  return { stages, rows: [...rows.values()] }
}

/** Microbiology results on this product (sterility, Gram stain…), when there are any. */
function Microbiology({ results }: { results: LisResult[] }) {
  const micro = results.filter((r) => categoryOf(r) === 'microbiology')
  if (!micro.length) return null
  return (
    <SectionCard size='md' title='Microbiology'>
      <div className='grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]'>
        {micro.flatMap((r) =>
          r.observations.map((o) => {
            const status = resultStatus(o.status ?? r.resultStatus)
            return (
              <div key={o.id} className='flex flex-col gap-1'>
                <div className='font-semibold'>{o.codeText ?? o.code}</div>
                <div className={cn('text-[12.5px]', MUTED)}>
                  {r.stage ? `${r.stage} sample` : 'Product sample'} {formatDate(collectedAt(r))} · {r.testName ?? r.testCode}
                </div>
                <div className='flex flex-wrap items-center gap-2'>
                  <span>{[o.value, o.units].filter(Boolean).join(' ') || '—'}</span>
                  <TonePill tone={status.tone}>{status.label}</TonePill>
                </div>
              </div>
            )
          }),
        )}
      </div>
    </SectionCard>
  )
}

/** One line per result: accession, panel, specimen, when, its first values, status. */
function LisSummary({ results, isLoading, isError }: { results: LisResult[] | undefined; isLoading: boolean; isError: boolean }) {
  const note = (text: string) => <p className={cn('m-0 px-5 pb-5 text-sm', MUTED)}>{text}</p>
  if (isLoading) return note('Loading laboratory results…')
  if (isError) return note('Laboratory results could not be loaded.')
  if (!results?.length) return note('No laboratory results filed against this product.')
  return (
    <RecordTable minWidth={640} head={['Accession', 'Panel', 'Specimen', 'Collected', 'Results', 'Status']}>
      {results.map((r) => {
        const status = resultStatus(r.resultStatus)
        const summary = r.observations
          .slice(0, 3)
          .map((o) => `${o.codeText ?? o.code} ${[o.value, o.units].filter(Boolean).join(' ')}`.trim())
          .join(' · ')
        return (
          <tr key={r.id}>
            <Td className='whitespace-nowrap font-mono text-[12.5px]'>{r.fillerOrderId || r.orderId || '—'}</Td>
            <Td className='font-semibold'>{r.testName ?? r.testCode}</Td>
            <Td>{r.stage ? `Product · ${r.stage.toLowerCase()}` : 'Product'}</Td>
            <Td className='whitespace-nowrap'>{formatDateTime(collectedAt(r))}</Td>
            <Td>
              {summary || '—'}
              {r.observations.length > 3 && <span className={MUTED}> · +{r.observations.length - 3} more</span>}
            </Td>
            <Td>
              <TonePill tone={status.tone}>{status.label}</TonePill>
            </Td>
          </tr>
        )
      })}
    </RecordTable>
  )
}
